import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const PotentialDriverModal = ({ isOpen, onClose, potentialDriver, onSave }) => {
    const { isDark } = useTheme();
    const [activeTab, setActiveTab] = useState('personal');
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        dob: '',
        tin: '',
        id_type: '',
        id_number: '',
        liscence_type: '',
        liscence_number: '',
        occupation: '',
        phone_number: '',
        email: '',
        address: '',
        landmark: '',
        marital_status: '',
        religion: '',
        name_of_spouse: '',
        spouse_occupation: '',
        number_of_children: '',
        number_of_dependencies: '',
        emc_name: '',
        emc_number: '',
        emc_address: '',
        emc_relation: '',
        emc_occupation: '',
        from: 'Application Form',
        status: 'PENDING',
    });
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (potentialDriver) {
                // Fetch full details for editing
                fetchPotentialDriverDetails(potentialDriver.id);
            } else {
                setFormData({
                    first_name: '',
                    last_name: '',
                    dob: '',
                    tin: '',
                    id_type: '',
                    id_number: '',
                    liscence_type: '',
                    liscence_number: '',
                    occupation: '',
                    phone_number: '',
                    email: '',
                    address: '',
                    landmark: '',
                    marital_status: '',
                    religion: '',
                    name_of_spouse: '',
                    spouse_occupation: '',
                    number_of_children: '',
                    number_of_dependencies: '',
                    emc_name: '',
                    emc_number: '',
                    emc_address: '',
                    emc_relation: '',
                    emc_occupation: '',
                    from: 'Application Form',
                    status: 'PENDING',
                });
            }
            setErrors([]);
            setActiveTab('personal');
        }
    }, [isOpen, potentialDriver]);

    const fetchPotentialDriverDetails = async (id) => {
        try {
            const response = await fetch(`/api/v1/potential_drivers/${id}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setFormData(data);
        } catch (error) {
            console.error('Error fetching potential driver details:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);

        try {
            const url = potentialDriver ? `/api/v1/potential_drivers/${potentialDriver.id}` : '/api/v1/potential_drivers';
            const method = potentialDriver ? 'PATCH' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: JSON.stringify({ potential_driver: formData }),
            });

            const data = await response.json();

            if (response.ok) {
                onSave(data);
                onClose();
            } else {
                setErrors(data.errors || ['An error occurred']);
            }
        } catch (error) {
            console.error('Error saving potential driver:', error);
            setErrors(['Failed to save potential driver. Please try again.']);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const tabs = [
        { id: 'personal', label: 'Personal Info', icon: '👤' },
        { id: 'contact', label: 'Contact', icon: '📞' },
        { id: 'marital', label: 'Marital & Family', icon: '👨‍👩‍👧‍👦' },
        { id: 'emergency', label: 'Emergency Contact', icon: '🚨' },
    ];

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        },
        header: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            color: isDark ? '#ffffff' : '#1f2937',
        },
        tabContainer: {
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            borderBottom: `2px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            overflowX: 'auto',
        },
        tab: (active) => ({
            padding: '0.75rem 1rem',
            cursor: 'pointer',
            borderBottom: active ? `3px solid #8b5cf6` : 'none',
            color: active ? (isDark ? '#ffffff' : '#1f2937') : (isDark ? '#9ca3af' : '#6b7280'),
            fontWeight: active ? 600 : 400,
            fontSize: '0.875rem',
            transition: '0.2s',
            whiteSpace: 'nowrap',
        }),
        formGroup: {
            marginBottom: '1.5rem',
        },
        label: {
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#d1d5db' : '#374151',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        select: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem',
        },
        errorList: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            marginTop: '2rem',
        },
        saveButton: {
            flex: 1,
            padding: '0.75rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        cancelButton: {
            flex: 1,
            padding: '0.75rem',
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={styles.header}>{potentialDriver ? 'Edit Potential Driver' : 'New Potential Driver'}</h2>

                {errors.length > 0 && (
                    <div style={styles.errorList}>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Tabs */}
                <div style={styles.tabContainer}>
                    {tabs.map(tab => (
                        <div
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={styles.tab(activeTab === tab.id)}
                        >
                            <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
                            {tab.label}
                        </div>
                    ))}
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Personal Info Tab */}
                    {activeTab === 'personal' && (
                        <>
                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>First Name *</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        required
                                        style={styles.input}
                                        placeholder="John"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Last Name *</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        required
                                        style={styles.input}
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Date of Birth</label>
                                    <input
                                        type="date"
                                        name="dob"
                                        value={formData.dob}
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>TIN</label>
                                    <input
                                        type="text"
                                        name="tin"
                                        value={formData.tin}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="TIN123456"
                                    />
                                </div>
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>ID Type</label>
                                    <select
                                        name="id_type"
                                        value={formData.id_type}
                                        onChange={handleChange}
                                        style={styles.select}
                                    >
                                        <option value="">Select ID type...</option>
                                        <option value="Ghana Card">Ghana Card</option>
                                        <option value="Passport">Passport</option>
                                        <option value="Voter ID">Voter ID</option>
                                        <option value="Driver License">Driver License</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={styles.label}>ID Number</label>
                                    <input
                                        type="text"
                                        name="id_number"
                                        value={formData.id_number}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="GHA-123456789-0"
                                    />
                                </div>
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>License Type</label>
                                    <select
                                        name="liscence_type"
                                        value={formData.liscence_type}
                                        onChange={handleChange}
                                        style={styles.select}
                                    >
                                        <option value="">Select license type...</option>
                                        <option value="Class A">Class A</option>
                                        <option value="Class B">Class B</option>
                                        <option value="Class C">Class C</option>
                                        <option value="Class D">Class D</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={styles.label}>License Number</label>
                                    <input
                                        type="text"
                                        name="liscence_number"
                                        value={formData.liscence_number}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="DL-987654"
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Occupation</label>
                                <input
                                    type="text"
                                    name="occupation"
                                    value={formData.occupation}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="Driver"
                                />
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>From</label>
                                    <input
                                        type="text"
                                        name="from"
                                        value={formData.from}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Application Form"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Status</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        style={styles.select}
                                    >
                                        <option value="PENDING">PENDING</option>
                                        <option value="APPROVED">APPROVED</option>
                                        <option value="REJECTED">REJECTED</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Contact Tab */}
                    {activeTab === 'contact' && (
                        <>
                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Phone Number *</label>
                                    <input
                                        type="tel"
                                        name="phone_number"
                                        value={formData.phone_number}
                                        onChange={handleChange}
                                        required
                                        style={styles.input}
                                        placeholder="+233123456789"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="john@example.com"
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Address</label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="123 Main Street, Accra"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Landmark</label>
                                <input
                                    type="text"
                                    name="landmark"
                                    value={formData.landmark}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="Near the Post Office"
                                />
                            </div>
                        </>
                    )}

                    {/* Marital & Family Tab */}
                    {activeTab === 'marital' && (
                        <>
                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Marital Status</label>
                                    <select
                                        name="marital_status"
                                        value={formData.marital_status}
                                        onChange={handleChange}
                                        style={styles.select}
                                    >
                                        <option value="">Select status...</option>
                                        <option value="Single">Single</option>
                                        <option value="Married">Married</option>
                                        <option value="Divorced">Divorced</option>
                                        <option value="Widowed">Widowed</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={styles.label}>Religion</label>
                                    <input
                                        type="text"
                                        name="religion"
                                        value={formData.religion}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Christian"
                                    />
                                </div>
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Name of Spouse</label>
                                    <input
                                        type="text"
                                        name="name_of_spouse"
                                        value={formData.name_of_spouse}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Jane Doe"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Spouse Occupation</label>
                                    <input
                                        type="text"
                                        name="spouse_occupation"
                                        value={formData.spouse_occupation}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Nurse"
                                    />
                                </div>
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Number of Children</label>
                                    <input
                                        type="text"
                                        name="number_of_children"
                                        value={formData.number_of_children}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="2"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Number of Dependencies</label>
                                    <input
                                        type="text"
                                        name="number_of_dependencies"
                                        value={formData.number_of_dependencies}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="4"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Emergency Contact Tab */}
                    {activeTab === 'emergency' && (
                        <>
                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Emergency Contact Name</label>
                                    <input
                                        type="text"
                                        name="emc_name"
                                        value={formData.emc_name}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Jane Doe"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Emergency Contact Number</label>
                                    <input
                                        type="tel"
                                        name="emc_number"
                                        value={formData.emc_number}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="+233244987654"
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Emergency Contact Address</label>
                                <input
                                    type="text"
                                    name="emc_address"
                                    value={formData.emc_address}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="456 Oak Avenue, Accra"
                                />
                            </div>

                            <div style={styles.formRow}>
                                <div>
                                    <label style={styles.label}>Relation to Driver</label>
                                    <input
                                        type="text"
                                        name="emc_relation"
                                        value={formData.emc_relation}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Sister"
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Emergency Contact Occupation</label>
                                    <input
                                        type="text"
                                        name="emc_occupation"
                                        value={formData.emc_occupation}
                                        onChange={handleChange}
                                        style={styles.input}
                                        placeholder="Nurse"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div style={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={styles.saveButton}
                        >
                            {loading ? 'Saving...' : (potentialDriver ? 'Update Potential Driver' : 'Create Potential Driver')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PotentialDriverModal;
